import {useEffect, useRef, useState} from 'react';
import ContactInformationComponent from "../FormElements/ContactInformationComponent.tsx";
import {useCustomer} from "../../../../layouts/SelfcareLayout.tsx";
import {FlowData, ProductInstance, RegisterFunction, SELFCARE_REPORT_FLOW_TYPE} from "../../../../util/types.ts";
import DescriptionTextFieldComponent from "../FormElements/DescriptionTextFieldComponent.tsx";
import {imageMap} from "../../../../images/imageMap.ts";
import OwnRouterComponent from "../FormElements/OwnRouterComponent.tsx";
import FormFieldsetComponent from "../FormElements/FormFieldsetComponent.tsx";
import AttachFilesComponent from "../FormElements/AttachFilesComponent.tsx";
import {ScrollToAnchorPoint} from "../../../../util/ScrollToAnchor.tsx";
import ErrorFlowButtons from "../FormElements/ErrorFlowButtonsComponent.tsx";
import {DispatchResetErrorReportPageEvent} from "../../../../util/events.ts";
import {submitFormFunction} from "../../../../util/SelfcareErrorReportTypes.ts";
import {
    anyComponentsWithValidationError,
    registerComponentsWithValidation,
    scrollToElement,
} from "../../../../util/helpers.ts";

export default function CoaxOfflineFlow({product, submitFunction}: { product: ProductInstance, submitFunction: submitFormFunction }) {
    const {customer} = useCustomer();

    const [flowData, setFlowData] = useState<FlowData>({
        flowtype: SELFCARE_REPORT_FLOW_TYPE.OFFLINE,
        technology: product.product.att_statistics_technology,
        email: customer.customer_contact.email,
        phone: customer.customer_contact.mobile_no,
        selectedContact: {email: false, phone: false},
        editingContact: {email: false, phone: false},
        descriptionText: '',
        ownRouterLights: '',
        coaxPlugType: undefined,
        attachedFiles: undefined,
    });

    const [submitting, setSubmitting] = useState<boolean>(false)
    const [submitError, setSubmitError] = useState<string>('');


    const handleSubmit = async () => {

        if (anyComponentsWithValidationError(flowData)) {
            return;
        }

        setSubmitting(true)
        submitFunction(customer.customer_id, product.product_instance_id, flowData)
            .then((result) => {
                setSubmitError(result)
                setSubmitting(false)
            })
    };

    return (
        <>
            <FormFieldsetComponent
                number={2}
                title={'Opsætning'}
                className={'equipment-setup'}
            >
                <OwnRouterComponent
                    product={product}
                    flowData={flowData}
                    setFlowData={setFlowData}
                    register={registerComponentsWithValidation}
                />

                <CoaxPlugType
                    flowData={flowData}
                    setFlowData={setFlowData}
                    register={registerComponentsWithValidation}
                />
            </FormFieldsetComponent>

            <FormFieldsetComponent
                number={3}
                title={'Uddyb'}
                className={'explain'}
            >
                <DescriptionTextFieldComponent
                    flowData={flowData}
                    setFlowData={setFlowData}
                    placeholder='Eks. "Ingen af mine enheder har kunnet tilgå internettet siden i går"'
                    register={registerComponentsWithValidation}
                />
                <AttachFilesComponent
                    setFlowData={setFlowData}
                    product={product}
                    flow={SELFCARE_REPORT_FLOW_TYPE.OFFLINE}
                />
            </FormFieldsetComponent>

            <FormFieldsetComponent
                number={4}
                title={'Kontakt'}
                className={'contact'}
            >
                <ContactInformationComponent
                    flowData={flowData}
                    setFlowData={setFlowData}
                    register={registerComponentsWithValidation}
                />
                {submitError.length > 0 &&
                    <div className={`input-container error`}>
                        <p className={'error-message'}>{submitError}</p>
                    </div>
                }
                <ErrorFlowButtons
                    onBack={() => DispatchResetErrorReportPageEvent()}
                    onSubmit={handleSubmit}
                    loading={submitting}
                />
            </FormFieldsetComponent>
        </>
    )
}

function CoaxPlugType({
                          flowData,
                          setFlowData,
                          register,
                      }: {
    flowData: FlowData;
    setFlowData: React.Dispatch<React.SetStateAction<FlowData>>;
    register: RegisterFunction;
}) {
    const [coaxPlugTypeError, setCoaxPlugTypeError] = useState<string>('');
    const coaxPlugTypeComponentTop = useRef() as React.MutableRefObject<HTMLDivElement>;

    const handleRadioChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const value = event.target.value;
        setFlowData((prev) => ({ ...prev, coaxPlugType: value }));

        if (value) {
            setCoaxPlugTypeError('');
        }
    };

    const plugTypeImage: Record<string, string> = {
        'dvu': imageMap.selfcare.internet.reportError.coax_plug_type_dvu,
        'med-gevind': imageMap.selfcare.internet.reportError.coax_plug_type_with_thread,
        'uden-gevind': imageMap.selfcare.internet.reportError.coax_plug_type_without_thread
    }

    const hasError = coaxPlugTypeError.length > 0;

    useEffect((): void => {
        register("DeviceErrorComponent",
            {
                hasErrors: (flowData: FlowData): boolean => {
                    if (!flowData.coaxPlugType?.trim()) {
                        setCoaxPlugTypeError("Vælg venligst typen af kabel-tv-stik din router er tilsluttet.");
                        return true
                    }
                    return false
                },
                scrollToError: (): void => {
                    scrollToElement(coaxPlugTypeComponentTop.current)
                },
            },
        )
    }, [register, coaxPlugTypeError]);

    return (
        <>
            <div className={`input-container input-container--coax-plug-types${hasError ? ' error' : ''}`} ref={coaxPlugTypeComponentTop}>
                <ScrollToAnchorPoint id={'coax-plug-type'} className={'id-anchor--selfcare-report-error'}/>
                <p>Hvilken slags kabel-tv-stik sidder din router i?*</p>
                <p className={'with-checkbox'}>
                    <input
                        type={'radio'}
                        name={'coax_plug_type'}
                        value={'dvu'}
                        id={'dvu'}
                        onChange={handleRadioChange}
                        checked={flowData.coaxPlugType === 'dvu'}
                    />
                    <label className={'checkbox-label checkbox-label--montserrat'} htmlFor={'dvu'}>
                        <span className={'text'}>DVU-stik</span>
                        <img
                            src={plugTypeImage['dvu']}
                            alt={'Illustration af hvordan en DVU-splitter ser ud.'}
                        />
                    </label>
                </p>
                <p className={'with-checkbox'}>
                    <input
                        type={'radio'}
                        name={'coax_plug_type'}
                        value={'med-gevind'}
                        id={'med-gevind'}
                        onChange={handleRadioChange}
                        checked={flowData.coaxPlugType === 'med-gevind'}
                    />
                    <label className={'checkbox-label checkbox-label--montserrat'} htmlFor={'med-gevind'}>
                        <span className={'text'}>Med gevind</span>
                        <img
                            src={plugTypeImage['med-gevind']}
                            alt={'Illustration af hvordan et kabel-tv-stik med gevind ser ud.'}
                        />
                    </label>
                </p>
                <p className={'with-checkbox'}>
                    <input
                        type={'radio'}
                        name={'coax_plug_type'}
                        value={'uden-gevind'}
                        id={'uden-gevind'}
                        onChange={handleRadioChange}
                        checked={flowData.coaxPlugType === 'uden-gevind'}
                    />
                    <label className={'checkbox-label checkbox-label--montserrat'} htmlFor={'uden-gevind'}>
                        <span className={'text'}>Uden gevind</span>
                        <img
                            src={plugTypeImage['uden-gevind']}
                            alt={'Illustration af hvordan et kabel-tv-stik uden gevind ser ud.'}
                        />
                    </label>
                </p>

                {hasError && <p className={'error-message'}>{coaxPlugTypeError}</p>}

                {(flowData.coaxPlugType === 'med-gevind' || flowData.coaxPlugType === 'uden-gevind') && (
                    <div className={'text-box mb-full'}>
                        <ul className={'list list--bullet'}>
                            <li>
                                Du skal tjekke, om du også har et DVU-stik i din bolig (billede 1).
                            </li>
                            <li>
                                Hvis ja, skal du sætte din router til DVU-stikket og give routeren tid til at oprette forbindelse, før du fortsætter fejlmeldingen.
                            </li>
                            <li>
                                Routeren skal nemlig altid sidde i DVU-stikket, såfremt du har sådan et i din bolig.
                            </li>
                        </ul>
                    </div>
                )}

                <div className={'coax-plug-images'}>
                    <label htmlFor={'dvu'} className={'coax-plug-image'}>
                        <span>DVU-stik</span>
                        <img
                            src={plugTypeImage['dvu']}
                            alt={'Illustration af hvordan en DVU-splitter ser ud.'}
                        />
                    </label>
                    <label htmlFor={'med-gevind'} className={'coax-plug-image'}>
                        <span>Med gevind</span>
                        <img
                            src={plugTypeImage['med-gevind']}
                            alt={'Illustration af hvordan et kabel-tv-stik med gevind ser ud.'}
                        />
                    </label>
                    <label htmlFor={'uden-gevind'} className={'coax-plug-image'}>
                        <span>Uden gevind</span>
                        <img
                            src={plugTypeImage['uden-gevind']}
                            alt={'Illustration af hvordan et kabel-tv-stik uden gevind ser ud.'}
                        />
                    </label>
                </div>
            </div>
        </>
    );
}
